import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, Calendar, Download, ChevronDown, ChevronUp } from "lucide-react";
import { API } from "@/App";
import api from "@/api/axios";
import { saveAs } from "file-saver";
import { useAuth } from "@/hooks/AuthContext";
import TimeEntryPopup from "@/components/TimeEntryPopup";
import dayjs from "dayjs";
import { toast } from "react-hot-toast";

function TimeAttendancePage({ month, userId }) {
  const { user } = useAuth();

  const [timeData, setTimeData] = useState({ data: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [expandedDays, setExpandedDays] = useState({});
  const [approving, setApproving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittingCorrectionByDay, setSubmittingCorrectionByDay] = useState({});
  const [teamMode, setTeamMode] = useState(false); // toggle for team-mode behavior
  const [selectedUser, setUser] = useState(false); // toggle for team-mode behavior

  const isTeamMode = teamMode || user.role === "TEAM";

  const [open, setOpen] = useState(false);

  const [defaultForm, setDefaultForm] = useState({
    project_id: "",
    task_id: "",
    date: "",
    start_at: "09:00",
    end_at: "17:00",
    break_minutes: 30,
    billable: true,
    location: "Office",
    notes: "",
  });

  const openForceAddTimesheet = (date) => {
    setDefaultForm({ ...defaultForm, date: dayjs(date, "DD-MMM-YYYY").format("YYYY-MM-DD") });
    setOpen(true);
  }

  const handleEdit = (entry) => {
    console.log(entry, "entry")
    setDefaultForm({
      _id: entry._id,
      project_id: entry.project_id,
      task_id: entry.task_id,
      date: entry.date,
      start_at: entry.start_time,
      end_at: entry.end_time,
      break_minutes: entry.break_minutes,
      billable: entry.billable,
      location: entry.location,
      notes: entry.notes,
    });
    setOpen(true);
  };



  const formatDate = (dateStr) => dateStr ? dateStr.replace(/-/g, " ") : "-";
  const formatDateForAPI = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case "OK": return "default";
      case "Exception": return "destructive";
      case "Leave": return "secondary";
      case "Suggested Correction!": return "outline";
      default: return "secondary";
    }
  };

  const toggleWeek = (weekNumber) => setExpandedWeeks(prev => ({ ...prev, [weekNumber]: !prev[weekNumber] }));
  const toggleDay = (weekNumber, date) => {
    const key = `${weekNumber}-${date}`;
    setExpandedDays(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isMonthlySubmitted = () => {
    if (!timeData?.data?.length) return false;

    const allEntries = timeData.data.flatMap(week =>
      week.days?.flatMap(day => day.timesheet?.entries || []) || []
    );

    if (!allEntries.length) return false;

    return allEntries.every(entry =>
      ["SUBMITTED", "APPROVED", "LOCKED"].includes(entry.status)
    );
  };

  const fetchTimeData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { month };
      if (isTeamMode) {
        params.user_id = user.id; // only own sheet
      } else if (userId) {
        params.user_id = userId; // admin selecting specific user
      }

      const response = await api.get(`${API}/time-entries/daily`, { params });
      setTimeData(response.data || { data: [] });
    } catch (err) {
      console.error(err);
      setError("Failed to fetch time data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimeData();
  }, [month, teamMode, userId]);

  // Auto-expand all weeks and days when data loads
  useEffect(() => {
    if (timeData?.data?.length) {
      const allWeeksExpanded = {};
      const allDaysExpanded = {};
      timeData.data.forEach(week => {
        allWeeksExpanded[week.week_number] = true;
        week.days?.forEach(day => {
          allDaysExpanded[`${week.week_number}-${day.date}`] = true;
        });
      });
      setExpandedWeeks(allWeeksExpanded);
      setExpandedDays(allDaysExpanded);
    }
  }, [timeData]);

  const fetchUserDetails = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await api.get(`${API}/users/${userId}`);
      setUser(res.data || {});
    } catch (err) {
      console.error("Failed to fetch tasks:", err);
    }
  }, [userId]);

  useEffect(() => {
    fetchUserDetails()
  }, [fetchUserDetails]);

  const handleApproveMonthly = async () => {
    if (!month) return;
    setApproving(true);
    try {
      const url = `${API}/time-entries/approve-monthly?month=${month}${userId ? `&user_id=${userId}` : ''}`;
      const response = await api.post(url);
      alert(response.data.message || "Timesheet approved successfully!");
      fetchTimeData();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to approve timesheet.");
    } finally {
      setApproving(false);
    }
  };

  const handleSubmitMonthly = async () => {
    if (!month) return;
    setSubmitting(true);
    try {
      const url = `${API}/time-entries/submit-monthly?month=${month}`;
      const response = await api.post(url);
      alert(response.data.message || "Timesheet submitted successfully!");
      fetchTimeData();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to submit timesheet.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitForDailyCorrection = async (date) => {
    if (!date) return;
    setSubmittingCorrectionByDay(prev => ({ ...prev, [date]: true }));
    try {
      const url = `${API}/time-entries/submit-for-correction-by-date?date=${date}`;
      const response = await api.post(url);
      alert(response.data.message || `Correction submitted for ${date}!`);
      fetchTimeData();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to submit daily correction.");
    } finally {
      setSubmittingCorrectionByDay(prev => ({ ...prev, [date]: false }));
    }
  };
  const isMonthlyApproved = () => {
    if (!timeData?.data?.length) return false;

    const allEntries = timeData.data.flatMap(week =>
      week.days?.flatMap(day => day.timesheet?.entries || []) || []
    );

    if (!allEntries.length) return false;

    // Check if all entries are already approved
    return allEntries.every(entry => entry.status === "APPROVED");
  };


  const exportToExcel = async () => {
    if (!userId) return;
    setLoading(true);

    try {
      const response = await api.get(`${API}/users/salary/preview`, {
        params: { user_id: userId, month }
      });

      const salaryData = response.data;
      toast.success("Salary fetched successfully!");

      if (Array.isArray(salaryData) && salaryData.length > 0) {
        // Extract headers from the first object
        const headers = Object.keys(salaryData[0]);

        // Build CSV rows
        const csvRows = [
          headers.join(","), // header row
          ...salaryData.map(row =>
            headers
              .map(header => {
                const val = row[header];
                // escape commas, quotes, newlines properly
                if (val === null || val === undefined) return "";
                const safe = String(val).replace(/"/g, '""');
                return `"${safe}"`;
              })
              .join(",")
          )
        ];

        // Join all rows with newlines
        const csvContent = csvRows.join("\n");

        // Create a downloadable CSV file
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `salary_preview_${selectedUser.name}_${month}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("Failed to fetch salary:", err);
      toast.error(err?.response?.data?.message || "Could not fetch salary.");
    } finally {
      setLoading(false);
    }
  };
  // const exportToExcel = () => {
  //   if (!timeData || !timeData.data?.length) return;
  //
  //   const headers = [
  //     "Date",
  //     "Day",
  //     "Project",
  //     "Task",
  //     "Hours",
  //     "Other Expenses/Total",
  //     "Misc Expense Description"
  //   ];
  //
  //   const rows = [];
  //   let totalHours = 0;
  //   let totalExpenses = 0;
  //
  //   timeData.data.forEach(week => {
  //     week.days?.forEach(day => {
  //       const dayExpenses = Array.isArray(day?.vouchers)
  //           ? day.vouchers.reduce((sum, v) => sum + (Number(v?.amount) || 0), 0)
  //           : 0;
  //
  //       totalExpenses += dayExpenses;
  //
  //       if (Array.isArray(day?.timesheet?.entries) && day.timesheet.entries.length > 0) {
  //         day.timesheet.entries.forEach(entry => {
  //           const hours = parseFloat(entry?.hours) || 0; // <-- Safe numeric conversion
  //           totalHours += hours;
  //
  //           rows.push([
  //             formatDate(day?.date),
  //             day?.day_of_week || "-",
  //             entry?.project || "-",
  //             entry?.task || "-",
  //             hours,
  //             dayExpenses,
  //             (day?.vouchers || [])
  //                 .map(v => v?.description || v?.project_id?.name || "")
  //                 .filter(Boolean)
  //                 .join(" & ")
  //           ]);
  //         });
  //       } else {
  //         rows.push([
  //           formatDate(day?.date),
  //           day?.day_of_week || "-",
  //           "-",
  //           "-",
  //           0,
  //           dayExpenses,
  //           (day?.vouchers || [])
  //               .map(v => v?.description || v?.project_id?.name || "")
  //               .filter(Boolean)
  //               .join(" & ")
  //         ]);
  //       }
  //     });
  //   });
  //
  //   // ✅ Add totals at bottom (force toFixed to avoid NaN from floating sums)
  //   rows.push([]);
  //   rows.push([
  //     "TOTAL",
  //     "",
  //     "",
  //     "",
  //     totalHours.toFixed(2), // Safe numeric formatting
  //     totalExpenses.toFixed(2),
  //     ""
  //   ]);
  //
  //   const csvContent = [
  //     headers.join(","),
  //     ...rows.map(r => r.map(v => `"${v}"`).join(","))
  //   ].join("\n");
  //
  //   saveAs(
  //       new Blob([csvContent], { type: "text/csv;charset=utf-8;" }),
  //       `timesheet-${month}.csv`
  //   );
  // };

  if (loading) return (
    <div className="page flex justify-center items-center h-64">
      <p>Loading time data...</p>
    </div>
  );

  if (error) return (
    <div className="page flex justify-center items-center h-64">
      <p className="text-destructive">{error}</p>
      <Button onClick={fetchTimeData} className="ml-4">Retry</Button>
    </div>
  );

  return (
    <div className="page">
      <div className="page-header flex justify-between items-start mb-4">
        <div>
          <h1>Time & Attendance</h1>
          <p>Your weekly overview of attendance and timesheet entries.</p>
        </div>
        <div className="flex flex-col gap-2 mt-6">
          {user.role === "ADMIN" && <Button onClick={exportToExcel} variant="outline" disabled={loading}>
            <Download className="mr-2 h-4 w-4" /> Export Excel
          </Button>}

          {/* Toggle Team Mode for Admin/PM */}
          {/* Toggle Team Mode only if Admin/PM AND no user is selected in parent */}
          {/*{['ADMIN', 'PM'].includes(user.role) && !userId && (*/}
          {/*  <Button variant="outline" onClick={() => setTeamMode(prev => !prev)}>*/}
          {/*    {teamMode ? "Switch to Admin View" : "Switch to Team Mode"}*/}
          {/*  </Button>*/}
          {/*)}*/}


          {['ADMIN', 'PM'].includes(user.role) && !isMonthlyApproved() && isMonthlySubmitted() && (
            <Button
              onClick={handleApproveMonthly}
              variant="outline"
              disabled={approving || !timeData.data.length}
            >
              {approving ? "Approving..." : "Approve Timesheet"}
            </Button>
          )}

          {!isMonthlyApproved() && !userId && <Button
            onClick={handleSubmitMonthly}
            variant="outline"
            disabled={submitting || !timeData.data.length}
          >
            {submitting ? "Submitting..." : isMonthlySubmitted() ? "Timesheet Already Submitted" : "Submit Timesheet"}
          </Button>}

          {isMonthlyApproved() && <p>Timesheet Already Approved!</p>}

        </div>
      </div>

      {timeData.data.length === 0 ? (
        <Card>
          <CardContent className="flex justify-center items-center h-40">
            <p className="text-muted-foreground">No time entries found for this month.</p>
          </CardContent>
        </Card>
      ) : (
        timeData.data.map(week => (
          <Card key={week.week_number} className="mb-6">
            <CardHeader className="bg-muted/30 py-3">
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg">
                  Week {week.week_number} ({formatDate(week.week_start)} - {formatDate(week.week_end)})
                </CardTitle>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4" />
                    <span>Attendance: {week.total_attendance_hours || 0}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4" />
                    <span>Timesheet: {week.total_timesheet_hours || 0}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => toggleWeek(week.week_number)}>
                    {expandedWeeks[week.week_number] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </CardHeader>

            {expandedWeeks[week.week_number] && (
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Check-In</TableHead>
                      <TableHead>Check-Out</TableHead>
                      <TableHead>Attendance</TableHead>
                      <TableHead>Timesheet</TableHead>
                      <TableHead>Variance</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {week.days?.map(day => {
                      const dayKey = `${week.week_number}-${day.date}`;
                      const isDayExpanded = expandedDays[dayKey];
                      const dayApiDate = formatDateForAPI(day.date);

                      const canSubmitCorrection = day.timesheet?.entries?.some(
                        e => ["SUBMITTED"].includes(e.status)
                      );

                      // Check if day is a holiday or weekend
                      const isHoliday = day.is_holiday || day.status === "Holiday" || ["Saturday", "Sunday"].includes(day.day_of_week);

                      return (
                        <React.Fragment key={day.date}>
                          <TableRow className={`${isDayExpanded ? "bg-muted/30" : ""} ${isHoliday ? "opacity-40 bg-gray-100 dark:bg-gray-800" : ""}`}>
                            <TableCell className="font-medium flex items-center">
                              <Button variant="ghost" size="sm" className="mr-2 h-6 w-6 p-0" onClick={() => toggleDay(week.week_number, day.date)}>
                                {isDayExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              </Button>
                              <div>
                                <div>{day.day_of_week || "-"}</div>
                                <div className="text-sm text-muted-foreground">{formatDate(day.date)}</div>
                              </div>
                            </TableCell>
                            <TableCell>{day.attendance?.check_in || "-"}</TableCell>
                            <TableCell>{day.attendance?.check_out || "-"}</TableCell>
                            <TableCell>{day.attendance?.total_hours || "0"}</TableCell>
                            <TableCell>{day.timesheet?.total_hours || "0"}</TableCell>
                            <TableCell>{day.variance || "-"}</TableCell>
                            <TableCell>
                              <Badge variant={getStatusVariant(day.status)}>{day.status || "-"}</Badge>
                            </TableCell>
                            {isTeamMode ? (
                              <TableCell>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={submittingCorrectionByDay[dayApiDate] || day.status === "APPROVED" || !canSubmitCorrection}
                                  onClick={() => handleSubmitForDailyCorrection(dayApiDate)}
                                >
                                  {day.status === "APPROVED"
                                    ? "Approved"
                                    : submittingCorrectionByDay[dayApiDate]
                                      ? "Submitting..."
                                      : "Submit for Correction"}
                                </Button>
                              </TableCell>
                            ) : (
                              <TableCell>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={day.status === "APPROVED" || !userId}
                                  onClick={() => openForceAddTimesheet(day.date)}
                                >
                                  Force Add Timesheet
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>

                          {isDayExpanded && (
                            <TableRow>
                              <TableCell colSpan={isTeamMode ? 8 : 7} className="p-0">
                                {/* Attendance Events */}
                                {day.attendance?.events?.length > 0 && (
                                  <div className="p-4 border-b">
                                    <h4 className="font-medium mb-2">Attendance Events</h4>
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>Type</TableHead>
                                          <TableHead>Time</TableHead>
                                          <TableHead>Method</TableHead>
                                          <TableHead>Occurred At</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {day.attendance.events.map((event, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell>{event.type}</TableCell>
                                            <TableCell>{event.time}</TableCell>
                                            <TableCell>{event.method || "-"}</TableCell>
                                            <TableCell>{new Date(event.occurred_at).toLocaleString()}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                )}

                                {/* Timesheet Entries */}
                                {day.timesheet?.entries?.length > 0 && (
                                  <div className="p-4">
                                    <h4 className="font-medium mb-2">Timesheet Entries</h4>
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>Project</TableHead>
                                          <TableHead>Task</TableHead>
                                          <TableHead>Start Time</TableHead>
                                          <TableHead>End Time</TableHead>
                                          <TableHead>Break</TableHead>
                                          <TableHead>Hours</TableHead>
                                          <TableHead>Billable</TableHead>
                                          <TableHead>Location</TableHead>
                                          <TableHead>Notes</TableHead>
                                          <TableHead>Status</TableHead>
                                          {user.role === "ADMIN" && <TableHead>Actions</TableHead>}
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {day.timesheet.entries.map((entry, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell>{entry.project}</TableCell>
                                            <TableCell>{entry.task}</TableCell>
                                            <TableCell>{entry.start_time}</TableCell>
                                            <TableCell>{entry.end_time}</TableCell>
                                            <TableCell>{entry.break_minutes}m</TableCell>
                                            <TableCell>{entry.hours}</TableCell>
                                            <TableCell>
                                              {entry.billable ? <Badge variant="default">Yes</Badge> : <Badge variant="secondary">No</Badge>}
                                            </TableCell>
                                            <TableCell>{entry.location}</TableCell>
                                            <TableCell className="max-w-xs truncate">{entry.notes}</TableCell>
                                            <TableCell><Badge variant="outline">{entry.status}</Badge></TableCell>
                                            {user.role === "ADMIN" && (
                                              <TableCell>
                                                <Button size="sm" variant="ghost" onClick={() => handleEdit(entry)}>
                                                  Edit
                                                </Button>
                                              </TableCell>
                                            )}
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                )}

                                {/* Empty state */}
                                {(!day.attendance?.events?.length && !day.timesheet?.entries?.length) && (
                                  <div className="p-4 text-center text-muted-foreground">
                                    No attendance events or timesheet entries for this day.
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            )}
          </Card>
        ))
      )}
      <TimeEntryPopup
        open={open}
        onClose={() => setOpen(false)}
        form={defaultForm}
        setForm={setDefaultForm}
        userId={userId}
        fetchMonthlyData={fetchTimeData}
        isTimeSlotValid={() => ({ isValid: true })}
        user={selectedUser}
      />
    </div>
  );
}

export default TimeAttendancePage;
